import React from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  Code2,
  Rocket,
  ArrowRight,
  Clock,
  Shield,
  MessageSquare
} from 'lucide-react';

const services = [
  {
    icon: Code2,
    title: "Full-Stack Development",
    description: "React, TypeScript, Node.js, and modern frameworks. From MVP to enterprise-scale applications.",
    features: ["React & Next.js", "TypeScript", "REST & GraphQL APIs", "Database Design"]
  },
  {
    icon: Zap,
    title: "AI-Accelerated Builds",
    description: "Leveraging Claude Code and Cursor to ship quality code faster than traditional development.",
    features: ["2-4 Week Delivery", "Clean Architecture", "Best Practices", "Documentation"]
  },
  {
    icon: Rocket,
    title: "Product Launch",
    description: "End-to-end product development from idea to deployed, production-ready application.",
    features: ["Deployment Setup", "CI/CD Pipelines", "Monitoring", "Maintenance"]
  }
];

const benefits = [
  {
    icon: Clock,
    title: "Ship Faster",
    description: "AI-powered development means your product launches in weeks, not months."
  },
  {
    icon: Shield,
    title: "Production Quality",
    description: "Clean, maintainable code that scales with your business."
  },
  {
    icon: MessageSquare,
    title: "Direct Communication",
    description: "Work directly with me - no middlemen, no miscommunication."
  }
];

const BuildWithUs = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How I Can Help
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Whether you need a full product built from scratch or want to augment your team,
            I deliver production-ready code that works.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative bg-gray-50 rounded-2xl p-8 hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#0088cc]/20"
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-xl bg-[#0088cc]/10 flex items-center justify-center mb-6 group-hover:bg-[#0088cc] group-hover:scale-110 transition-all duration-300">
                <service.icon className="w-7 h-7 text-[#0088cc] group-hover:text-white transition-colors" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
              <p className="text-gray-600 mb-6">{service.description}</p>

              {/* Features */}
              <ul className="space-y-2">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#4caf50]" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Why Work With Me?
              </h3>
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-[#0088cc]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{benefit.title}</h4>
                      <p className="text-gray-400">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - CTA */}
            <div className="text-center lg:text-left">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <h4 className="text-xl font-semibold text-white mb-3">
                  Ready to Build Something?
                </h4>
                <p className="text-gray-400 mb-6">
                  Let's discuss your project and see how I can help bring your idea to life.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0088cc] text-white rounded-lg hover:bg-[#006699] transition-colors font-medium"
                  >
                    Start a Project
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/solutions"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors font-medium"
                  >
                    Learn More
                  </Link>
                </div>
                <p className="mt-4 text-sm text-gray-500">
                  Free consultation - No commitment required
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuildWithUs;
